import { useState } from "react";
import { useFulfillmentTypeUpdate } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeUpdate";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { DELIVERY_METHOD } from "../../constants/routes";
import { Button, Icon, InputContainer } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useFulfillmentType } from "../../hooks/api/v2/fulfillmentType/useFulfillmentType";
import { SpinnerLegacy } from "../../components";
import { FulfillmentTypeChargeCapUpdateDialog } from "../deliveryCharges/FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeChargeCapUpdateDialog";
import { capitalize, formatCurrency, pluralize } from "@sellar-io/common";
import { FulfillmentTypeGetResponseType } from "@sellar-io/api-v2/types";
import { DeliveryMethodContainerRulesDialog } from "./components/DeliveryMethodContainerRulesDialog";
import { InputLabel } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";

export const DeliveryMethodDetails = () => {
  const deliveryMethodId = useParamAsNumber(
    DELIVERY_METHOD,
    "deliveryMethodId",
  );

  const [editedName, setEditedName] = useState<string | null>(null);

  const [chargeCapActive, setChargeCapActive] = useState(false);

  const { data: deliveryMethod, isLoading } = useFulfillmentType(
    deliveryMethodId,
    {
      enabled: !!deliveryMethodId,
    },
  );

  const name = editedName ?? deliveryMethod?.name ?? "";
  const showNameSave =
    editedName !== null &&
    editedName !== deliveryMethod?.name &&
    editedName.length > 0;

  const fulfillmentTypeUpdateMutation =
    useFulfillmentTypeUpdate(deliveryMethodId);

  if (isLoading) return <SpinnerLegacy />;

  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        <form
          className="mx-auto flex w-full max-w-[920px] flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            fulfillmentTypeUpdateMutation.mutate(
              {
                name,
              },
              {
                onSuccess: () => {
                  setEditedName(null);
                },
              },
            );
          }}
        >
          <InputLabel>Name</InputLabel>
          <div className="w-full">
            <div className="flex">
              <InputContainer
                className={twMerge(
                  "relative flex w-full items-center gap-1",
                  showNameSave && "rounded-r-none",
                )}
              >
                <InputText
                  value={name}
                  onChange={(e) => setEditedName(e.target.value)}
                />
              </InputContainer>
              {showNameSave && (
                <Button
                  type="submit"
                  size="small"
                  className="rounded-l-none px-4"
                  loading={fulfillmentTypeUpdateMutation.isPending}
                >
                  Update
                </Button>
              )}
            </div>
          </div>
          <p className="text-zinc-500">
            <span className="font-semibold">
              {deliveryMethod?.fulfillmentType === "haulier"
                ? "Pallet"
                : capitalize(deliveryMethod?.fulfillmentType || "")}
            </span>{" "}
            type delivery method
          </p>
        </form>

        <DeliveryMethodContainerRulesSection deliveryMethod={deliveryMethod} />

        {deliveryMethod?.fulfillmentType !== "haulier" && (
          <div className="flex flex-col gap-2">
            <InputLabel>Delivery charge cap</InputLabel>
            {deliveryMethod?.deliveryChargeCap ? (
              <div className="flex w-full items-center justify-between">
                {formatCurrency(deliveryMethod.deliveryChargeCap)}
                <span
                  className="cursor-pointer text-sm text-red-500 underline"
                  onClick={() => {
                    fulfillmentTypeUpdateMutation.mutate({
                      deliveryChargeCap: 0,
                    });
                  }}
                >
                  Remove
                </span>
              </div>
            ) : (
              <Button onClick={() => setChargeCapActive(true)}>Add</Button>
            )}
          </div>
        )}
      </div>
      <FulfillmentTypeChargeCapUpdateDialog
        active={chargeCapActive}
        onDismiss={() => setChargeCapActive(false)}
        deliveryChargeCap={deliveryMethod?.deliveryChargeCap}
        fulfillmentTypeId={deliveryMethod?.id}
      />
    </>
  );
};

const DeliveryMethodContainerRulesSection = ({
  deliveryMethod,
}: {
  deliveryMethod?: FulfillmentTypeGetResponseType;
}) => {
  const [active, setActive] = useState(false);

  const rulesCount = deliveryMethod?.DeliveryMethodContainerRules.length ?? 0;

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex w-full items-center justify-between gap-2">
          <InputLabel>Container rules</InputLabel>
          <Button
            color="transparent"
            size="small"
            onClick={() => setActive(true)}
          >
            <Icon icon="PENCIL" size={0.7} />
          </Button>
        </div>
        <div
          className="flex cursor-pointer flex-col gap-2 rounded-lg bg-zinc-100 p-4 text-sm"
          onClick={() => setActive(true)}
        >
          <span className="italic">
            {rulesCount === 0
              ? "All containers available"
              : `${rulesCount} ${pluralize("container", rulesCount)} not available`}
          </span>
        </div>
      </div>
      <DeliveryMethodContainerRulesDialog
        active={active}
        setActive={setActive}
        deliveryMethod={deliveryMethod}
      />
    </>
  );
};
